import aaTrackEvent from './utils/aaTrackEvent';

/**
 * A custom element in charge of adding base JS functionality to the header.
 * Examples: all header tracking, updating header content when a BBB changes.
 *
 * @module iabbb-header
 */
class Header extends HTMLElement {
  connectedCallback() {
    self.addEventListener('hf:set-bbb', this.handleSetBbb);
    self.addEventListener('iabbb-country-custom-select:select', this.syncCountryCustomSelects);
    this.addAnalyticsEventListeners();
  }

  disconnectedCallback() {
    self.removeEventListener('hf:set-bbb', this.handleSetBbb);
    self.addEventListener('iabbb-country-custom-select:select', this.syncCountryCustomSelects);
    this.removeAnalyticsEventListeners();
  }

  /**
   * Emit a custom event
   * @param {string} name - The event name
   * @param {object} detail - Any details to pass along with the event
   */
  emit = (name, detail = {}) => {
    // Create a new event
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: detail,
    });

    // Dispatch the event
    return this.dispatchEvent(event);
  };

  handleSetBbb = (e) => {
    this.updateUtilityLinks(e.detail.utilityLinks);
    this.updatePopularCategories(e.detail.popularCategories);
    this.updateMainNavLinks(e.detail.mainNavLinks);
  };

  syncCountryCustomSelects = (e) => {
    this.emit('iabbb-country-custom-select:manual-set-option', { value: e.detail.value });
  };

  addAnalyticsEventListeners = () => {
    const utilityLinks = this.querySelectorAll('.utility-bar a');

    for (const utilityLink of utilityLinks) {
      utilityLink.addEventListener('click', this.handleUtilityLinkClick);
    }

    self.addEventListener('iabbb-locale-selector:click-location-link', this.handleClickLocaleSelectorLocationLink);

    self.addEventListener('iabbb-search-bar:execute', this.handleSearchExecute);

    self.addEventListener('iabbb-find-autocomplete:type', this.handleFindAutocompleteType);
    self.addEventListener('iabbb-near-autocomplete:type', this.handleNearAutocompleteType);

    self.addEventListener('iabbb-find-autocomplete:select-option', this.handleFindAutocompleteSelect);
    self.addEventListener('iabbb-near-autocomplete:select-option', this.handleNearAutocompleteSelect);
  };

  removeAnalyticsEventListeners = () => {
    const utilityLinks = this.querySelectorAll('.utility-bar a');

    for (const utilityLink of utilityLinks) {
      utilityLink.removeEventListener('click', this.handleUtilityLinkClick);
    }

    self.removeEventListener(
      'iabbb-locale-selector:click-location-link',
      this.handleClickLocaleSelectorLocationLink,
    );

    self.removeEventListener('iabbb-search-bar:execute', this.handleSearchExecute);

    self.removeEventListener('iabbb-find-autocomplete:type', this.handleFindAutocompleteType);
    self.removeEventListener('iabbb-near-autocomplete:type', this.handleNearAutocompleteType);

    self.removeEventListener('iabbb-find-autocomplete:select-option', this.handleFindAutocompleteSelect);
    self.removeEventListener('iabbb-near-autocomplete:select-option', this.handleNearAutocompleteSelect);
  };

  updateUtilityLinks = (utilityLinks) => {
    const utilityBar = this.querySelector('.utility-bar');

    if (utilityLinks.length > 0) {
      utilityBar.removeAttribute('data-is-empty');
      utilityBar.innerHTML = `<div class="wrapper">
        <ul>
          ${utilityLinks.map((link) => `<li><a href="${link.href}">${link.label}</a></li>`).join('')}
        </ul>
      </div>`;
    } else {
      utilityBar.setAttribute('data-is-empty', '');
      utilityBar.innerHTML = '';
    }
  };

  updateMainNavLinks = (updatedMainNavLinks) => {
    if (!updatedMainNavLinks) {
      return;
    }

    if (updatedMainNavLinks.length === 0) {
      return;
    }

    const navMenuList = document.querySelector('#headerNavMenu ul');
    navMenuList.innerHTML = updatedMainNavLinks
      .map((link, index) => {
        const hasSubnav = link.links && link.links.length > 0;
        const linkId = `mainNavLink-${index + 1}`;

        // Changes to markup must also be done in navListItem.njk
        return `<li${hasSubnav ? ` class="has-subnav"` : ''}${link.icon ? ` data-with-icon=""` : ''}>
          <a class="dtm-header-nav-menu-link" href="${link.href}"${hasSubnav ? ` id="${linkId}"` : ''}>
            ${link.icon || ''}${link.label}
          </a>
          ${
            hasSubnav
              ? `
            <button aria-expanded="false" aria-labelledby="${linkId}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false" height="1em" width="1em"><path d="M241 337c-9.4 9.4-24.6 9.4-33.9 0L47 177c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l143 143L367 143c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9L241 337z"></path></svg>
            </button>
            <ul>
              ${link.links
                .map(
                  (subNavLink) =>
                    `<li>
                    <a class="dtm-header-subnav-menu-link" href="${subNavLink.href}">${subNavLink.label}</a>
                  </li>`,
                )
                .join('')}
            </ul>
          `
              : ''
          }
        </li>`;
      })
      .join('');
  };

  updatePopularCategories = (popularCategories) => {
    window.headerFooterData = {
      ...(window.headerFooterData || {}),
      bbb: {
        ...(window.headerFooterData?.bbb || {}),
        popularCategories,
      },
    };
  };

  handleSearchExecute = () => {
    aaTrackEvent('searchCompleted');
    aaTrackEvent('searchExecuted');
  };

  handleUtilityLinkClick = (e) => {
    aaTrackEvent('headerLinkClick', { title: e.target.innerText, url: e.target.href });
  };

  handleFindAutocompleteType = (e) => {
    this.updateSearchDataLayer({
      find_query: e.detail.query,
      find_method: 'type',
      used_type_aheads: 'false',
    });
  };

  handleNearAutocompleteType = (e) => {
    this.updateSearchDataLayer({
      near_query: e.detail.query,
      near_method: 'type',
      used_type_aheads: 'false',
    });
  };

  handleFindAutocompleteSelect = (e) => {
    this.updateSearchDataLayer({
      find_query: e.detail.value,
      find_method: 'selected',
      used_type_aheads: 'true',
    });
  };

  handleNearAutocompleteSelect = (e) => {
    this.updateSearchDataLayer({
      near_query: e.detail.value,
      near_method: 'selected',
      used_type_aheads: 'true',
    });
  };

  updateSearchDataLayer = (updatedSearchDataLayer) => {
    if (window.webDigitalData?.search_info) {
      window.webDigitalData.search_info = {
        ...window.webDigitalData.search_info,
        updatedSearchDataLayer,
      };
    }
  };
}

customElements.define('iabbb-header', Header);
